/**
 * Today-screen module layout: the movable sections (hero, week strip, quick
 * add, spotlight, overdue, snoozed, today, upcoming days) and how they deal
 * into 1–3 columns. Pure — the store persists a `TodayLayout`, `TodayGrid`
 * renders it. The global `order` is the single source of truth: it IS the
 * 1-column reading order, and each column displays its own modules in that
 * same order, so collapsing to fewer columns never scrambles anything.
 */

export const TODAY_MODULES = ["hero", "week", "quickAdd", "nextUp", "overdue", "snoozed", "today", "upcoming"] as const;
export type TodayModuleId = (typeof TODAY_MODULES)[number];

export interface TodayLayout {
  /** Global reading order — top→bottom in one column. */
  order: TodayModuleId[];
  /** Preferred column per module (0-based; omitted = 0). Clamped to the live
   *  column count, so a col-2 preference degrades to the rightmost column. */
  column: Partial<Record<TodayModuleId, number>>;
  /** Columns a module spans (omitted = 1). Clamped to the live count, so a
   *  wide module degrades gracefully on a narrow window. */
  span: Partial<Record<TodayModuleId, number>>;
}

export const TODAY_MAX_COLUMNS = 3;

export const DEFAULT_TODAY_LAYOUT: TodayLayout = {
  order: [...TODAY_MODULES],
  column: { overdue: 1, snoozed: 1, upcoming: 2 },
  span: {},
};

/** Column count for a container width — each column keeps a comfortable
 *  reading width before another opens. */
export function columnsForWidth(px: number): number {
  if (px >= 1640) return 3;
  if (px >= 1100) return 2;
  return 1;
}

/** The column `id` renders in for a live column count. */
export function moduleColumn(layout: TodayLayout, id: TodayModuleId, cols: number): number {
  return Math.max(0, Math.min(layout.column[id] ?? 0, cols - 1));
}

/** Columns `id` spans for a live column count (1 when unset or single-column). */
export function moduleSpan(layout: TodayLayout, id: TodayModuleId, cols: number): number {
  return Math.max(1, Math.min(layout.span[id] ?? 1, cols));
}

/** Deal the ordered modules into `cols` columns, preserving global order
 *  inside each column. This span-less projection is useful for validating the
 *  one-column reading order; {@link packModules} owns rendered placement. */
export function distributeModules(layout: TodayLayout, cols: number): TodayModuleId[][] {
  const out: TodayModuleId[][] = Array.from({ length: cols }, () => []);
  for (const id of layout.order) out[moduleColumn(layout, id, cols)]!.push(id);
  return out;
}

/** A packed placement: grid column, span, and y offset in px. */
export interface PackedModule {
  id: TodayModuleId;
  col: number;
  span: number;
  top: number;
}

/** Whether two packed column footprints intersect. */
export function packedRangesOverlap(
  aCol: number,
  aSpan: number,
  bCol: number,
  bSpan: number,
): boolean {
  return aCol < bCol + bSpan && bCol < aCol + aSpan;
}

/**
 * Modules occupying any part of a target column range, ordered by their
 * visual midpoint. A wide module must resolve against its WHOLE footprint:
 * looking only at its left edge lets a tall card in a covered right-hand
 * column push the committed placement far below the drop preview.
 */
export function packedRangeStack(
  placed: ReadonlyArray<PackedModule>,
  col: number,
  span: number,
  heightOf: (id: TodayModuleId) => number,
  except?: TodayModuleId,
): PackedModule[] {
  return placed
    .filter((item) => item.id !== except && packedRangesOverlap(item.col, item.span, col, span))
    .sort((a, b) => {
      const byMiddle = (a.top + heightOf(a.id) / 2) - (b.top + heightOf(b.id) / 2);
      return byMiddle !== 0 ? byMiddle : a.top - b.top;
    });
}

/**
 * Skyline packing — the organic 2D flow: walk the global order and rest each
 * module on the highest free spot of its column range. A wide module rests on
 * the TALLEST of its spanned columns and pushes only those columns down;
 * every other column keeps flowing beside it. `heights` are measured pixel
 * heights — modules not yet measured are skipped (they place next frame).
 * Within one column, placements always follow global order top-to-bottom.
 */
export function packModules(
  layout: TodayLayout,
  cols: number,
  heights: (id: TodayModuleId) => number | undefined,
  gap: number,
  include?: (id: TodayModuleId) => boolean,
): { placed: PackedModule[]; height: number } {
  const skyline: number[] = Array.from({ length: cols }, () => 0);
  const placed: PackedModule[] = [];
  for (const id of layout.order) {
    if (include && !include(id)) continue;
    const h = heights(id);
    if (h == null) continue;
    const span = moduleSpan(layout, id, cols);
    const col = Math.min(moduleColumn(layout, id, cols), cols - span);
    const top = Math.max(...skyline.slice(col, col + span));
    placed.push({ id, col, span, top });
    for (let k = col; k < col + span; k++) skyline[k] = top + h + gap;
  }
  const tallest = Math.max(0, ...skyline);
  return { placed, height: tallest > 0 ? tallest - gap : 0 };
}

/** Where a drop lands relative to its new neighbors: before `beforeId`, after
 *  `afterId`, or (both unset) at the very end of the order. */
export interface MoveAnchor {
  beforeId?: TodayModuleId | undefined;
  afterId?: TodayModuleId | undefined;
}

/**
 * Relocate `id` to `toCol`, splicing the global order next to the anchor
 * module — anchors (not indexes) because bands can split a column across
 * several stacks, so "index 0 of this stack" is only meaningful relative to
 * the modules actually rendered around the drop point.
 */
export function moveModule(
  layout: TodayLayout,
  id: TodayModuleId,
  toCol: number,
  anchor: MoveAnchor,
  cols: number,
): TodayLayout {
  const maxCol = Math.max(0, cols - moduleSpan(layout, id, cols));
  const col = Math.max(0, Math.min(maxCol, Math.round(toCol)));
  const column = { ...layout.column, [id]: col };
  const without = layout.order.filter((m) => m !== id);
  let insertAt = without.length;
  if (anchor.beforeId && without.includes(anchor.beforeId)) insertAt = without.indexOf(anchor.beforeId);
  else if (anchor.afterId && without.includes(anchor.afterId)) insertAt = without.indexOf(anchor.afterId) + 1;
  return { order: [...without.slice(0, insertAt), id, ...without.slice(insertAt)], column, span: layout.span };
}

/** The next span in the width cycle (1 → 2 → … → live column count → 1). */
export function nextSpan(layout: TodayLayout, id: TodayModuleId, cols: number): number {
  const current = moduleSpan(layout, id, cols);
  return current >= Math.min(cols, TODAY_MAX_COLUMNS) ? 1 : current + 1;
}

/** Parse a persisted layout: drop unknown ids, dedupe, append any module the
 *  stored order predates, clamp columns + spans. Anything unparseable ⇒ the
 *  default. */
export function normalizeLayout(raw: unknown): TodayLayout {
  if (!raw || typeof raw !== "object") return DEFAULT_TODAY_LAYOUT;
  const v = raw as Partial<TodayLayout>;
  const known = new Set<string>(TODAY_MODULES);
  const order: TodayModuleId[] = [];
  const seen = new Set<string>();
  if (Array.isArray(v.order)) {
    for (const m of v.order) {
      if (typeof m === "string" && known.has(m) && !seen.has(m)) {
        order.push(m as TodayModuleId);
        seen.add(m);
      }
    }
  }
  for (const m of TODAY_MODULES) if (!seen.has(m)) order.push(m);

  const span: Partial<Record<TodayModuleId, number>> = {};
  if (v.span && typeof v.span === "object" && !Array.isArray(v.span)) {
    for (const m of TODAY_MODULES) {
      const s = (v.span as Record<string, unknown>)[m];
      if (typeof s === "number" && Number.isFinite(s) && s > 1) {
        span[m] = Math.min(TODAY_MAX_COLUMNS, Math.round(s));
      }
    }
  }

  const column: Partial<Record<TodayModuleId, number>> = {};
  if (v.column && typeof v.column === "object" && !Array.isArray(v.column)) {
    for (const m of TODAY_MODULES) {
      const c = (v.column as Record<string, unknown>)[m];
      if (typeof c === "number" && Number.isFinite(c)) {
        column[m] = Math.max(0, Math.min(TODAY_MAX_COLUMNS - 1, Math.round(c)));
      }
    }
    return { order, column, span };
  }
  return { order, column: { ...DEFAULT_TODAY_LAYOUT.column }, span };
}
